import { UploadFile } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Typography, IconButton, Stack } from "@mui/material";
import { useEffect, useRef, useState } from "react";

import Colors from "../../../global/Colors";
import HNSColors from "../../HNSColors";
import FullyRoundedButton from "../buttons/FullyRoundedButton";

const DragAndDropContainer = ({ initValue, onValueChange }) => {
  const [selectedFile, setSelectedFile] = useState(initValue);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setSelectedFile(initValue);
  }, [initValue]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      onValueChange(e, file);
    }
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      onValueChange(e, file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleCancel = (e) => {
    setSelectedFile(null);
    onValueChange(e, null);
  };

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        alignItems: "center",
        backgroundColor: isDragging ? Colors.gray[100] : "transparent",
        border: `2px dashed ${Colors.gray[200]}`,
        borderRadius: "12px",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minHeight: "auto",
        padding: "3rem 1rem",
        position: "relative",
        textAlign: "center",
      }}
    >
      {selectedFile ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1">{selectedFile.name}</Typography>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) : (
        <div className="flex flex-col items-center justify-center space-y-2">
          <UploadFile className="text-3xl" sx={{ color: HNSColors.primary[500] }} />
          <Typography variant="subtitle1" className="!my-4 text-gray-400">
            Drag and drop a file here or
          </Typography>
          <FullyRoundedButton
            bgColor="bg-white"
            borderColor="border-purple-600"
            borderWidth="border-[1px]"
            buttonText="Browse"
            handleOnClick={triggerFileInput}
            horizontalMargin="mx-0"
            hoverBgColor={HNSColors.secondary[900]}
            hoverBorderColor={HNSColors.secondary[500]}
            hoverTextColor={HNSColors.secondary[500]}
            textColor="text-purple-600"
            variant="outlined"
            verticalMargin="!my-0"
          />
          <input
            id="file-input"
            ref={fileInputRef}
            type="file"
            hidden
            onChange={(event) => handleChange(event)}
            style={{ position: "absolute", top: "0", right: "0", left: "0", bottom: "0" }}
            className="w-full h-full m-0"
          />
        </div>
      )}
    </Box>
  );
};

export default DragAndDropContainer;
